<script>
	import * as Yup from "yup";
	import Forms, { useValidations } from "../../ursCommon/Forms.svelte";
	import { FormGroup, Input } from "@sveltestrap/sveltestrap";
	import { baseUrl, isValidDate } from "../../ursCommon/Common.svelte";
	import Dialog from "../../ursCommon/Dialog.svelte";
	import { Loader } from "../../ursCommon/GlobalLoader.svelte";
	import * as MedicationInfo from "./Medications.svelte";
	import DateRangeInput from "../DateRange.svelte";
	import { parse } from "svelte/compiler";

	const medications = MedicationInfo.Medications;
	const previousMedications = $state(MedicationInfo.PreviousMedications);

	// an array of all the selected previous medication names
	let selectedPreviousMedications = $derived(
		previousMedications.filter((pm) => pm.checked).map((pm) => pm.name.replace('\u00AE', '')),
	);
	const { validateField, validateForm, getErrors, resetErrors, removeError } =
		useValidations();
	const FREETEXT_REGEX = /^(?=.{1,200}$)[A-Za-z0-9 ,.:;!?'\"()&-]+$/;
	const FREETEXT_WARNING = "Please remove invalid characters (Letters, numbers and punctuation allowed).";

	let { disabled } = $props();

	let doses = $state([]);
	let prompt;
	let dateRangeInput;
	let submissionDate = new Date();

	let errors = $derived(getErrors());
	let formHasErrors = $derived(
		Object.keys(errors).length || dateRangeInput.hasErrors() ? true : false,
	);

	let formData = $state({
		medication: "",
		dose: "",
		otherMedication: "",
		isOtherMedicationRequired: false,
		formFrequency: "",
		beginDate: "",
		endDate: "",
		selectedPreviousMedications: "",
		otherPrevDrugName: "",
		isOtherPriorMedicationRequired: false,
	});

	const MAX_BEGIN_OFFSET_DAYS = 45;

	let beginDate = $state(formatDate(submissionDate));
	let maxBeginDate = $derived(addDays(formData.beginDate, 45));
	let maxEndDate = $derived(addMonths(formData.beginDate, 12));
	const doseDisabled = $derived(!formData.medication || doses.length === 0);

	const isDoseRequired = $derived(!doseDisabled);

	const isOtherMedicationRequired = $derived(
		medications.find((m) => m.name === "Other").name === formData.medication,
	);
	const isOtherPriorMedicationRequired = $derived(
		formData.selectedPreviousMedications.includes("Other"),
	);

	const baseSchema = Yup.object({
		medication: Yup.string().required("Please select a medication"),
		formFrequency: Yup.string().required("Please select a form / frequency"),
	});

	function validateendDate() {
		const beginDate = formData.beginDate;
		if (!beginDate) return true;
		const limit = addMonths(new Date(beginDate), 12);
		return formData.endDate <= limit;
	}

	const doseRequiredSchema = Yup.object({
		dose: Yup.string().required("Please select a dose"),
	});

	const otherMedicationRequiredSchema = Yup.object({
		otherMedication: Yup.string()
			.matches(FREETEXT_REGEX, FREETEXT_WARNING)
			.required("Please enter the other medication"),
	});

	const otherPriorMedicationRequiredSchema = Yup.object({
		otherPrevDrugName: Yup.string()
			.matches(FREETEXT_REGEX, FREETEXT_WARNING)
			.required("Please enter any other medications"),
	});

	let formSchema = $derived.by(() => {
		let schema = baseSchema;
		if (isDoseRequired) {
			schema = schema.concat(doseRequiredSchema);
		}
		if (isOtherMedicationRequired) {
			schema = schema.concat(otherMedicationRequiredSchema);
		}
		if (isOtherPriorMedicationRequired) {
			schema = schema.concat(otherPriorMedicationRequiredSchema);
		}
		return schema;
	});

	export function clearAllErrors() {
		resetErrors();
	}

	export function getValues() {
		formData.beginDate = dateRangeInput.getValues()?.beginDate;
		formData.endDate = dateRangeInput.getValues()?.endDate;
		formData.isOtherMedicationRequired = isOtherMedicationRequired;
		formData.isOtherPriorMedicationRequired = isOtherPriorMedicationRequired;
		formData.selectedPreviousMedications = selectedPreviousMedications.join(', ');
		return formData;
	}

	export function setValues(data) {
		formData.medication = data.medication;
		formData.dose = data.dose;
		formData.otherMedication = data.otherMedication;
		formData.formFrequency = data.formFrequency;
		formData.selectedPreviousMedications = data.selectedPreviousMedications;
		formData.otherPrevDrugName = data.otherPrevDrugName;
		formData.isOtherMedicationRequired = isOtherMedicationRequired;
		formData.isOtherPriorMedicationRequired = isOtherPriorMedicationRequired;
		resetErrors();
	}

	export function hasErrors() {
		if (formHasErrors) console.log(JSON.stringify(errors));
		return formHasErrors;
	}

	export async function validate() {
		await dateRangeInput.validate();
		await validateForm(formSchema, formData);
	}

	function formatDate(date) {
		const newDate = new Date(date);
		const year = newDate.getFullYear();
		const month = (newDate.getMonth() + 1).toString().padStart(2, "0");
		const day = newDate.getDate().toString().padStart(2, "0");
		return `${year}-${month}-${day}`;
	}

	function addMonths(startDate, numberOfMonths) {
		const newDate = parseLocalDate(startDate);
		newDate.setMonth(newDate.getMonth() + numberOfMonths);
		return formatDate(newDate);
	}

	function addDays(startDate, numberOfDays) {
		const newDate = parseLocalDate(startDate);
		newDate.setDate(newDate.getDate() + numberOfDays);
		return formatDate(newDate);
	}

	// must pass a date string in the format of yyyy-MM-DD
	function parseLocalDate(yyyyMmDd) {
		if (yyyyMmDd === undefined) return new Date(1, 1, 1);
		const [y, m, d] = yyyyMmDd.split("-").map(Number);
		return new Date(y, m - 1, d); // local time
	}

	function handleMedicationChange(e) {
		if (formData.medication) {
			doses = medications.find((m) => m.name === formData.medication).doses;
			formData.dose = "";
		}
		validateField(formSchema, "medication", formData);
	}

	function handleOtherMedicationChange() {
		validateField(formSchema, "otherMedication", formData);
	}

	function handleChange(fieldName, fieldValue) {
		const vars = new Map();
		vars.set(fieldName, fieldValue);
		const data = Object.fromEntries(vars);
		for (const key in data) {
			validateField(formSchema, key, formData);
		}
	}

	function handleBeginDateChange(e) {
		const val = e.target.value;
		formData.endDate = addMonths(e.target.value, 12);
		validateField(formSchema, "beginDate", formData);
		validateField(formSchema, "endDate", formData);
	}

	function resetOtherPrevDrug(e) {
		var val = e.target.value;
		const isNoneChecked = previousMedications.find((pm) => pm.name === 'None').checked;
		// if None was clicked and currently checked.
		if (val === 'None' && isNoneChecked) {
			// uncheck all other checked
			for (let med of previousMedications){
				if (med.name !== val) {
					med.checked = false;
				}
			}
		}

		// if None wasn't clicked and None is currently checked
		if (val !== 'None' && isNoneChecked) {
			previousMedications.find((pm) => pm.name === 'None').checked = false;
		}
		formData.selectedPreviousMedications = selectedPreviousMedications.join(", ");
	}
</script>

<main>
	<div class="row">
		<div class="col-xs-12 col-sm-6 col-md-3">
			<FormGroup floating label="Medication">
				<Input
					id="medication"
					name="medication"
					bind:value={formData.medication}
					on:change={(e) => handleMedicationChange(e.target.value)}
					invalid={Boolean(errors.medication)}
					feedback={errors.medication}
					{disabled}
					type="select"
				>
					{#each medications as m}
						<option value={m.name}>{m.name}</option>
					{/each}
				</Input>
			</FormGroup>
		</div>
		{#if isOtherMedicationRequired}
			<div class="col-xs-12 col-sm-6 col-md-3">
				<FormGroup floating label="Other Medication">
					<Input
						id="OtherMedication"
						name="OtherMedication"
						type="text"
						on:input={(e) => handleChange("otherMedication", e.target.value)}
						on:blur={(e) => handleChange("otherMedication", e.target.value)}
						placeholder="Please supply a drug name"
						bind:value={formData.otherMedication}
						invalid={Boolean(errors.otherMedication)}
						feedback={errors.otherMedication}
						{disabled}
					/>
				</FormGroup>
			</div>
		{/if}
		<div class="col-xs-12 col-sm-6 col-md-3">
			<FormGroup floating label="Dose">
				<Input
					id="dose"
					name="dose"
					disabled={doseDisabled}
					bind:value={formData.dose}
					on:change={(e) => handleChange("dose", e.target.value)}
					invalid={Boolean(errors.dose)}
					feedback={errors.dose}
					type="select"
				>
					<option value=""></option>
					{#each doses as dose}
						<option value={dose}>{dose}</option>
					{/each}
				</Input>
			</FormGroup>
		</div>
		<div class="col-xs-12 col-sm-6 col-md-3">
			{#each ["Daily Tablet", "Weekly Injection"] as value}
				<Input
					type="radio"
					name="frequency"
					{value}
					label={value}
					invalid={Boolean(errors.formFrequency)}
					bind:group={formData.formFrequency}
					on:change={(e) => handleChange("formFrequency", e.target.value)}
					disabled={disabled}
				/>
			{/each}
			{#if Boolean(errors?.formFrequency)}
				<div id="formFrequencyError" class="radioButtonInvalid">
					{errors.formFrequency}
				</div>
			{/if}
	</div>
	<div class="row">
		<div class="col-sm-12">
			<h5>Authorization Request Dates</h5>
		</div>
	</div>
	<DateRangeInput bind:this={dateRangeInput} {disabled} />
	<hr/>
	<div class="row">
		<div class="col-sm-12">
			<h5>
				Previous GLP-1 medication(s) with which the patient has had therapy
			</h5>
		</div>
	</div>
	<div class="row">
		{#each previousMedications as prevMed}
			<div class="col-sm-4 col-md-3 col-lg-3">
				<Input
					id={prevMed.name.replace('\u00AE', '')}
					name={prevMed.name.replace('\u00AE', '')}
					label={prevMed.name}
					type="checkbox"
					bind:checked={prevMed.checked}
					value={prevMed.name.replace('\u00AE', '')}
					on:change={(e) => resetOtherPrevDrug(e)}
					{disabled}
				/>
			</div>
		{/each}
		{#if selectedPreviousMedications.length && selectedPreviousMedications.includes("Other")}
			<div class="row">
				<div class="col-12 mt-1">
					<FormGroup floating label="Other Previous Drug">
						<Input
							id="otherPrevDrugName"
							name="otherPrevDrugName"
							type="text"
							placeholder="Please supply a drug name"
							on:input={(e) => handleChange("otherPrevDrugName", e.target.value)}
							on:blur={(e) => handleChange("otherPrevDrugName", e.target.value)}
							bind:value={formData.otherPrevDrugName}
							invalid={Boolean(errors.otherPrevDrugName)}
							feedback={errors.otherPrevDrugName}
							{disabled}
						/>
					</FormGroup>
				</div>
			</div>
		{/if}
	</div>
</main>